import { useCallback, useMemo } from 'react';

import { logger } from '@zep/core-logger';

import { useBuildAiQuizByQuery } from '@/api/ai/useBuildAiQuiz';
import { quizLevelToApiQuizDifficulty } from '@/shared/features/ai-quiz-generation/constants';
import { type BuildPromptOptions } from '@/shared/features/ai-quiz-generation/types';

import {
  AI_GENERATION_EVENT_KEY,
  getPromptData,
  getStandardCodeData,
} from '../utils/getAiEventData';

import { type AchievementStandard, type UseAiByMethodParams } from './types';
import { useBuildAiQueryErrorAlert } from './useBuildAiQueryErrorAlert';

/**
 * Query 방식 AI 퀴즈 생성 커스텀 훅
 *
 * 파일/비디오 첨부 없이 사용자 텍스트 입력으로 직접 퀴즈를 생성하는
 * Query 생성 방식을 위한 UseAiByMethodReturn 인터페이스 구현체
 *
 * 주요 기능:
 * - 프롬프트 검증 (비어있지 않은지, 최대 길이)
 * - 기존 useBuildAiQuizByQuery mutation 사용
 * - 콜백을 통해 성공/오류 처리를 상위 컴포넌트에 위임
 * - File/YouTube 방식과의 일관성을 위한 공통 인터페이스 구현
 *
 * @param params - onGenerate, onError 콜백을 포함한 훅 파라미터
 * @returns UseAiByMethodReturn 인터페이스 구현
 */
export const useAiByQuery = (params: UseAiByMethodParams) => {
  const {
    onGenerateSuccess,
    onGenerateStart,
    onGenerateError,
    tryCount,
    generationId,
  } = params;

  // API 클라이언트의 기존 mutation 사용
  const { mutateAsync, isPending } = useBuildAiQuizByQuery();

  const queryErrorAlert = useBuildAiQueryErrorAlert();

  /**
   * 프롬프트로부터 퀴즈 생성 처리
   * 입력을 검증하고, API mutation을 호출하며, 적절한 콜백을 실행
   */
  const handleGenerate = useCallback(
    async (
      prompt: string,
      options: BuildPromptOptions,
      standards: AchievementStandard[],
      topicId?: string,
      // omit (undefined) 시 서버 기본 모델 결정. mutation factory 가 snake_case 변환.
      generationMode?: 'fast' | 'thorough',
      // Stage 1 UI cohort. wire 시 ui_cohort 로 변환. dropdown 일 때만
      // 서버가 Stage 2 backend_cohort (real/placebo) 를 평가한다.
      uiCohort?: 'as_is' | 'dropdown',
    ) => {
      onGenerateStart(tryCount > 0 ? true : false);
      const startTime = Date.now();
      try {
        const response = await mutateAsync({
          userQuery: prompt,
          topicId: tryCount > 0 ? null : topicId ?? null,
          type: options.quizType,
          difficulty: quizLevelToApiQuizDifficulty(options.quizLevel),
          quizCount: options.quizCount,
          tryCount,
          locale: options.language,
          selectedStandardCodes: standards.map(standard => standard.code),
          generationId,
          generationMode,
          uiCohort,
        });
        // 생성된 퀴즈 아이템과 함께 상위 컴포넌트의 onGenerate 콜백 호출
        onGenerateSuccess(response.questions);
        const completedEventPayload = {
          ...options,
          ...getPromptData(
            {
              type: 'none' as const,
              data: null,
            },
            prompt,
          ),
          ...getStandardCodeData(standards),
          is_retry: tryCount > 0,
          latency_ms: Date.now() - startTime,
          requested_question_count: options.quizCount,
          sample_question: response.questions[0].question,
          sample_answer: response.questions[0].answer,
          sample_options: response.questions[0].options,
          sample_type: response.questions[0].type,
          generation_id: response.generationId || generationId,
        };
        logger.track(AI_GENERATION_EVENT_KEY, completedEventPayload);
        logger.track('biz_ai_generation_completed', completedEventPayload);
      } catch (error: unknown) {
        onGenerateError();
        logger.track('biz_ai_generation_failed', {
          generation_id: generationId,
          error_type: error instanceof Error ? error.name : 'unknown',
          attempt_no: tryCount,
          latency_ms: Date.now() - startTime,
          source_type: 'none',
        });
        queryErrorAlert(
          error as Error,
          tryCount > 0
            ? 'ai_문제생성_주제_다시생성_에러'
            : 'ai_문제생성_주제_시작_에러',
        );
      }
    },
    [
      onGenerateSuccess,
      onGenerateStart,
      onGenerateError,
      tryCount,
      queryErrorAlert,
      mutateAsync,
      generationId,
    ],
  );

  // Query 방식은 첨부물이 없으므로 정적 값 반환
  const attachment = null;
  const isAttached = false;

  // Query 방식용 no-op 함수 (사용되지 않음)
  const handleAttach = useCallback(async () => {
    // Query 방식은 첨부물을 지원하지 않음
  }, []);

  const handleDetach = useCallback(() => {
    // Query 방식은 첨부물을 지원하지 않음
  }, []);

  // 불필요한 리렌더링 방지를 위해 반환 객체 메모이제이션
  return useMemo(
    () => ({
      attachment,
      isAttached,
      handleAttach,
      handleDetach,
      handleGenerate,
      isGenerating: isPending,
    }),
    [
      attachment,
      isAttached,
      handleAttach,
      handleDetach,
      handleGenerate,
      isPending,
    ],
  );
};
